import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Div, Text, Spinner,
  FormItem, Input, Button, Snackbar,
} from '@vkontakte/vkui';
import { Icon28CheckCircleOutline, Icon28CancelOutline } from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import CountdownHero from '../../ui/CountdownHero';
import ThemeSwitcher from '../../ui/ThemeSwitcher';

const THUMBS = [
  { id: 1, label: 'Букеты', image: '/countdown/bg-1.jpg' },
  { id: 2, label: 'Кольца', image: '/countdown/bg-2.jpg' },
  { id: 3, label: 'Цветы', image: '/countdown/bg-3.jpg' },
  { id: 4, label: 'Стол', image: '/countdown/bg-4.jpg' },
  { id: 5, label: 'Бокалы', image: '/countdown/bg-5.jpg' },
  { id: 6, label: 'Романтика', image: '/countdown/bg-6.jpg' },
  { id: 7, label: 'Градиент', image: '' },
  { id: 8, label: 'Ночь', image: '' },
  { id: 9, label: 'Розы', image: '' },
  { id: 10, label: 'Закат', image: '' },
] as const;

const SHAPES = [
  { id: 1, label: 'Капсула' },
  { id: 2, label: 'Плашка' },
  { id: 3, label: 'Рамка' },
  { id: 4, label: 'Чистый' },
] as const;

const ANIMS = [
  { id: 'hearts', label: 'Сердечки' },
  { id: 'pulse', label: 'Пульс' },
  { id: 'glow', label: 'Свечение' },
  { id: 'bounce', label: 'Лёгкость' },
  { id: 'off', label: 'Выкл' },
] as const;

type AnimId = (typeof ANIMS)[number]['id'];

export default function CountdownPage() {
  const navigate = useNavigate();
  const { currentWedding, isLoading, countdown, loadCountdown, saveCountdown } = useWeddingStore();

  const [now, setNow] = useState(() => Date.now());
  const [phrase, setPhrase] = useState('');
  const [imageId, setImageId] = useState(1);
  const [shapeId, setShapeId] = useState(4);
  const [animId, setAnimId] = useState<AnimId>('hearts');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // load when wedding becomes available
  useEffect(() => {
    if (currentWedding?.id) {
      loadCountdown().catch((e) => console.error('loadCountdown', e));
    }
  }, [currentWedding?.id, loadCountdown]);

  useEffect(() => {
    if (!countdown) return;
    setPhrase(countdown.phrase || '');
    setImageId(countdown.image_id || 1);
    setShapeId(countdown.shape_id || 4);
    // animation: true → hearts (default), false → off
    setAnimId(countdown.animation === false ? 'off' : 'hearts');
  }, [countdown]);

  async function handleSave() {
    if (!currentWedding) {
      setSnack({ text: 'Сначала выберите свадьбу', ok: false });
      return;
    }
    setSaving(true);
    try {
      await saveCountdown({
        phrase: phrase.trim(),
        image_id: imageId,
        shape_id: shapeId,
        animation: animId !== 'off',
      });
      setSnack({ text: 'Сохранено', ok: true });
    } catch (e) {
      console.error('save countdown', e);
      setSnack({ text: e instanceof Error ? e.message : 'Не удалось сохранить', ok: false });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Отсчёт
      </PanelHeader>

      {isLoading && !currentWedding ? (
        <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
      ) : (
        <Div>
          {/* ===== HERO preview (live) ===== */}
          <CountdownHero
            wedding={currentWedding}
            countdown={{
              id: countdown?.id || '',
              wedding_id: currentWedding?.id || '',
              image_id: imageId,
              shape_id: shapeId,
              phrase,
              animation: animId !== 'off',
            }}
            now={now}
          />

          {/* ===== Settings grid ===== */}
          <div className="cd-grid" style={{ marginTop: 12 }}>
            {/* phrase — wide */}
            <div className="cd-card cd-card--wide">
              <p className="cd-card__title">Фраза</p>
              <FormItem style={{ padding: 0, marginBottom: 0 }} top="Над цифрами">
                <Input
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="До нашей свадьбы осталось…"
                  maxLength={80}
                />
              </FormItem>
            </div>

            {/* backgrounds — wide (horizontal scroll) */}
            <div className="cd-card cd-card--wide">
              <p className="cd-card__title">Фон · свадебный</p>
              <div className="cd-thumbs">
                {THUMBS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`cd-thumb${imageId === t.id ? ' cd-thumb--active' : ''}`}
                    style={
                      t.image
                        ? { backgroundImage: `url(${t.image})` }
                        : { background: t.id === 7 ? 'linear-gradient(145deg,#7C5CFC,#F472B6)' : t.id === 8 ? 'linear-gradient(160deg,#0F172A,#7C3AED)' : t.id === 9 ? 'linear-gradient(135deg,#FDF2F8,#BE185D)' : 'linear-gradient(145deg,#FFF7ED,#FB7185)' }
                    }
                    onClick={() => setImageId(t.id)}
                    aria-label={t.label}
                    title={t.label}
                  />
                ))}
              </div>
            </div>

            {/* shape */}
            <div className="cd-card">
              <p className="cd-card__title">Форма цифр</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SHAPES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`cd-chip${shapeId === s.id ? ' cd-chip--active' : ''}`}
                    onClick={() => setShapeId(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* animation */}
            <div className="cd-card">
              <p className="cd-card__title">Анимация</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ANIMS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`cd-chip${animId === a.id ? ' cd-chip--active' : ''}`}
                    onClick={() => setAnimId(a.id)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Button
            size="l"
            mode="primary"
            stretched
            loading={saving}
            disabled={!currentWedding}
            onClick={handleSave}
            style={{ marginTop: 16, marginBottom: 24 }}
          >
            Сохранить
          </Button>

          {!currentWedding && (
            <Text style={{ textAlign: 'center', color: 'var(--vkui--color_text_secondary)', marginBottom: 16 }}>
              Нет активной свадьбы — создайте её на главной
            </Text>
          )}

          <ThemeSwitcher />
        </Div>
      )}

      {snack && (
        <Snackbar
          onClose={() => setSnack(null)}
          onClosed={() => setSnack(null)}
          before={
            snack.ok
              ? <Icon28CheckCircleOutline fill="var(--colibri-success)" />
              : <Icon28CancelOutline fill="var(--colibri-danger)" />
          }
        >
          {snack.text}
        </Snackbar>
      )}
    </Panel>
  );
}
